import { useQuery } from "@tanstack/react-query";
import { Check, Copy } from "lucide-react";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { StatusBadge } from "@/design-system/StatusBadge";
import { readFeeBalance, type FeeBalanceResponse } from "@/shared/api";
import { isZero, money, percent } from "@/shared/money/money";

function day(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function State({ data }: { readonly data: FeeBalanceResponse }): React.JSX.Element {
  if (data.exhausted) return <StatusBadge tone="danger">лимит долга — новые сделки стоят</StatusBadge>;
  if (data.low) return <StatusBadge tone="warning">долг у лимита</StatusBadge>;
  return <StatusBadge tone="ready">в порядке</StatusBadge>;
}

function HowToPay({ uid }: { readonly uid: string | null | undefined }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-2" data-testid="fee-how-to-pay">
      <p className="text-text-primary">Как пополнить</p>
      <ol className="flex list-decimal flex-col gap-1 pl-5">
        <li>В Bybit откройте <b>Активы → Перевод → Внутренний перевод</b>.</li>
        <li>Получатель — <b>UID</b>, монета — <b>USDT</b>, сумма — сколько хотите внести.</li>
        <li>
          <span>UID Skryptos: </span>
          <code className="rounded bg-background-base px-2 py-1 text-data-mono" data-testid="fee-platform-uid">{uid ?? "появится после первой синхронизации"}</code>
          {uid != null && <Button
            type="button"
            variant="ghost"
            aria-label="Скопировать UID Skryptos"
            onClick={() => { void navigator.clipboard.writeText(uid).then(() => { setCopied(true); }); }}
          >
            {copied ? <Check aria-hidden="true" className="size-4" /> : <Copy aria-hidden="true" className="size-4" />}
            {copied ? "Скопировано" : "Скопировать"}
          </Button>}
        </li>
      </ol>
      <p className="text-helper-sm text-text-muted">Перевод засчитается сам в течение 5 минут, если он отправлен с того же счёта Bybit, что подключён к Skryptos.</p>
    </div>
  );
}

/** The account's prepaid platform fee: what is left, how to top it up, what was paid. */
export function FeeBalancePanel(): React.JSX.Element | null {
  const query = useQuery({
    queryKey: ["fee-balance"],
    retry: false,
    refetchInterval: 60_000,
    queryFn: async () => {
      const result = await readFeeBalance({ cache: "no-store" });
      if (result.data === undefined) throw new Error("fee balance unavailable");
      return result.data;
    },
  });
  const data = query.data;
  // An account that pays no platform fee has no balance to show.
  if (data !== undefined && isZero(data.rate)) return null;
  return (
    <Panel title="Баланс комиссии" testId="fee-balance" trailing={data === undefined ? undefined : <State data={data} />}>
      <div className="flex flex-col gap-3 text-body-main text-text-secondary">
        {query.isPending ? <p role="status">Загружаю…</p> : query.isError || data === undefined ? (
          <p role="alert" className="text-status-warning">Баланс сейчас недоступен — состояние неизвестно.</p>
        ) : (
          <>
            <p className="text-heading-sm text-text-primary" data-testid="fee-balance-value">{money(data.balance)} USDT</p>
            {/* A debt up to the platform's limit is allowed (owner, 2026-10-17):
                what matters to the client is how much is left before it stops. */}
            {data.credit_limit !== undefined && !isZero(data.credit_limit) && (
              <p data-testid="fee-available">
                До остановки новых сделок: <b className="text-text-primary">{money(data.available ?? "0")} USDT</b> · лимит долга {money(data.credit_limit)} USDT
              </p>
            )}
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 tabular-nums" data-testid="fee-breakdown">
              {data.credited !== undefined && !isZero(data.credited) && (
                <>
                  <dt>Бонусы платформы</dt>
                  <dd className="text-status-positive" data-testid="fee-welcome-credit">
                    {money(data.credited)} USDT, осталось {money(data.bonus_left ?? "0")} USDT
                  </dd>
                </>
              )}
              <dt>Пополнено</dt>
              <dd>{money(data.deposited)} USDT</dd>
              {data.owed !== undefined && !isZero(data.owed) && (
                <>
                  <dt>Не покрыто (долг)</dt>
                  <dd className="text-status-negative" data-testid="fee-owed">{money(data.owed)} USDT</dd>
                </>
              )}
              <dt>Списано комиссии</dt>
              <dd>{money(data.accrued)} USDT{data.bonus_spent !== undefined && !isZero(data.bonus_spent) ? ` (из бонуса ${money(data.bonus_spent)})` : ""}</dd>
            </dl>
            <p>Комиссия платформы — {percent(data.rate, 0)}, и только с прибыли закрытых сделок выше прошлого максимума. Сначала расходуется бонус, потом пополнения. Баланс может уйти в минус до лимита долга — пополнять каждую мелочь не нужно. Когда до лимита останется 50 USDT, пришлём напоминание в Telegram; на лимите бот перестанет открывать новые сделки.</p>
            {data.exhausted && <p role="alert" className="text-status-warning">Долг по комиссии достиг лимита: бот не открывает новые сделки, пока баланс не пополнен. Открытые сделки сопровождаются до закрытия в штатном режиме.</p>}
            {data.low && <p role="alert" className="text-status-warning">Долг подходит к лимиту — рекомендуем пополнить, чтобы бот не останавливал новые сделки.</p>}
            <HowToPay uid={data.platform_uid} />
            {data.deposits.length > 0 && (
              <div className="flex flex-col gap-1" data-testid="fee-deposits">
                <p className="text-text-primary">Пополнения</p>
                <ul className="flex flex-col gap-1">
                  {data.deposits.map((item) => (
                    <li key={`${item.at}-${item.amount}`} className="flex justify-between gap-4 tabular-nums">
                      <span>{day(item.at)}</span>
                      <span className="text-text-primary">+{money(item.amount)} USDT</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </Panel>
  );
}
